import React from 'react';
import { cn } from '../../../utils/cn';

/**
 * ProgressBar - Linear progress indicator
 *
 * A polished progress bar with optional shimmer animation, gradient fills,
 * and multiple size variants. Used to visualize completion percentages.
 *
 * @example
 * ```tsx
 * <ProgressBar
 *   value={75}
 *   color="emerald"
 *   size="md"
 *   showShimmer
 *   gradient
 * />
 * ```
 */

export interface ProgressBarProps {
  /** Progress value (0-100) */
  value: number;
  /** Color variant */
  color?: 'blue' | 'violet' | 'emerald' | 'amber' | 'cyan' | 'rose' | 'gray';
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Enable animated shimmer effect */
  showShimmer?: boolean;
  /** Use gradient instead of solid color */
  gradient?: boolean;
  /** Additional CSS classes */
  className?: string;
}

const sizeMap = {
  sm: 'h-1.5',
  md: 'h-2',
  lg: 'h-3',
};

const solidColorMap = {
  blue: 'bg-chart-blue',
  violet: 'bg-chart-violet',
  emerald: 'bg-chart-green',
  amber: 'bg-chart-yellow',
  cyan: 'bg-accent-cyan',
  rose: 'bg-chart-red',
  gray: 'bg-chart-slate',
};

const gradientColorMap = {
  blue: 'bg-gradient-to-r from-[var(--chart-blue)] to-[var(--accent-blue-hover)]',
  violet: 'bg-gradient-to-r from-[var(--chart-violet)] to-[color-mix(in_srgb,var(--chart-violet),black_20%)]',
  emerald: 'bg-gradient-to-r from-[var(--status-success)] to-[var(--status-success-hover)]',
  amber: 'bg-gradient-to-r from-[var(--status-warning)] to-[var(--status-warning-hover)]',
  cyan: 'bg-gradient-to-r from-[var(--accent-cyan)] to-[color-mix(in_srgb,var(--accent-cyan),black_15%)]',
  rose: 'bg-gradient-to-r from-[var(--status-error)] to-[var(--status-error-hover)]',
  gray: 'bg-gradient-to-r from-[var(--chart-slate)] to-[color-mix(in_srgb,var(--chart-slate),black_20%)]',
};

/**
 * ProgressBar Component
 *
 * Features:
 * - Animated shimmer effect for visual interest
 * - Gradient or solid color options
 * - Multiple size variants
 * - Smooth transitions
 * - Accessible with proper ARIA attributes
 */
export const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  color = 'blue',
  size = 'md',
  showShimmer = false,
  gradient = false,
  className,
}) => {
  // Clamp value between 0 and 100
  const clampedValue = Math.min(Math.max(value, 0), 100);

  const fillColorClass = gradient
    ? gradientColorMap[color]
    : solidColorMap[color];

  return (
    <div
      className={cn(
        'bg-bg-tertiary dark:bg-bg-secondary rounded-full overflow-hidden',
        sizeMap[size],
        className
      )}
      role="progressbar"
      aria-valuenow={clampedValue}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className={cn(
          'h-full rounded-full transition-all duration-300',
          fillColorClass,
          {
            'progress-bar': showShimmer,
          }
        )}
        style={{ width: `${clampedValue}%` }}
      />
    </div>
  );
};

ProgressBar.displayName = 'ProgressBar';
